import axios from 'axios';

/**
 * API Base URL configuration for Vendor Dashboard
 * 
 * Uses VITE_API_URL environment variable if set, otherwise falls back to localhost.
 * 
 * To configure for different environments:
 * - Development: Create .env.local with VITE_API_URL=http://localhost:8000/api/v1
 * - Production: Set VITE_API_URL=https://your-api-domain.com/api/v1
 * 
 * Note: VITE_ prefix is required for Vite to expose the variable to the browser
 */
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

// Debug logging (always log to help debug in production)
console.log('🔧 Vendor Dashboard API Configuration:', {
  baseURL: API_BASE_URL,
  environment: import.meta.env.MODE,
  viteApiUrl: import.meta.env.VITE_API_URL,
  isDev: import.meta.env.DEV,
});

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (error: any) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
  
  registerVendor: (vendorData: any) =>
    api.post('/auth/register-vendor', vendorData),
  
  getCurrentUser: () =>
    api.get('/auth/me'),

  getSettings: () =>
    api.get('/auth/settings'),

  updateSettings: (data: {
    notifications?: {
      inApp?: boolean;
      email?: boolean;
      sms?: boolean;
      types?: {
        lead?: boolean;
        review?: boolean;
        listing?: boolean;
        store?: boolean;
        system?: boolean;
      };
    };
    privacy?: {
      showEmail?: boolean;
      showPhone?: boolean;
      allowMessages?: boolean;
    };
    regional?: {
      language?: string;
      timezone?: string;
      currency?: string;
    };
  }) =>
    api.patch('/auth/settings', data),

  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    bio?: string;
    avatar?: string;
  }) =>
    api.patch('/auth/me', data),
  
  logout: () =>
    api.post('/auth/logout'),
  
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  
  resendVerification: () =>
    api.post('/auth/resend-verification'),
};

// Vendor Dashboard API
export const vendorAPI = {
  getDashboard: () =>
    api.get('/vendor/dashboard'),
  
  getStores: (params?: any) =>
    api.get('/vendor/stores', { params }),
  
  getListings: (params?: any) =>
    api.get('/vendor/listings', { params }),
  
  getLeads: (params?: any) =>
    api.get('/vendor/leads', { params }),
  
  updateLeadStatus: (leadId: string, data: any) =>
    api.patch(`/vendor/leads/${leadId}/status`, data),
  
  getReviews: (params?: any) =>
    api.get('/vendor/reviews', { params }),
  
  getAnalytics: (params?: any) =>
    api.get('/vendor/analytics', { params }),
  
  getNotifications: (params?: any) =>
    api.get('/vendor/notifications', { params }),

  markNotificationAsRead: (notificationId: string) =>
    api.patch(`/vendor/notifications/${notificationId}/read`),

  markAllNotificationsAsRead: () =>
    api.patch('/vendor/notifications/read-all'),

  bulkMarkNotificationsAsRead: (ids: string[]) =>
    api.patch('/vendor/notifications/read', { ids }),

  deleteNotification: (notificationId: string) =>
    api.delete(`/vendor/notifications/${notificationId}`),

  bulkDeleteNotifications: (ids: string[]) =>
    api.request({ method: 'delete', url: '/vendor/notifications', data: { ids } }),
};

// Store API
export const storeAPI = {
  create: (storeData: any) =>
    api.post('/stores', storeData),
  
  getMyStores: () =>
    api.get('/stores/my-stores/all'),
  
  getById: (storeId: string) =>
    api.get(`/stores/${storeId}`),
  
  update: (storeId: string, storeData: any) =>
    api.patch(`/stores/${storeId}`, storeData),
  
  getStats: (storeId: string) =>
    api.get(`/stores/${storeId}/stats`),
  
  uploadImages: (storeId: string, formData: FormData) =>
    api.post(`/stores/${storeId}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  uploadDocuments: (storeId: string, formData: FormData) =>
    api.post(`/stores/${storeId}/upload-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  delete: (storeId: string) =>
    api.delete(`/stores/${storeId}`),
};

// Listing API
export const listingAPI = {
  create: (listingData: any) =>
    api.post('/listings', listingData),
  
  getAll: (params?: any) =>
    api.get('/listings', { params }),

  /** Authenticated: vendor/admin listing by MongoDB id (not public slug). */
  getById: (listingId: string) =>
    api.get(`/listings/id/${listingId}`),

  getMyListings: (params?: Record<string, unknown>) =>
    api.get('/listings/my-listings/all', { params }),
  
  update: (listingId: string, listingData: any) =>
    api.patch(`/listings/${listingId}`, listingData),
  
  delete: (listingId: string) =>
    api.delete(`/listings/${listingId}`),
  
  uploadImages: (listingId: string, formData: FormData) =>
    api.post(`/listings/${listingId}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
};

// Category API
export const categoryAPI = {
  getAll: (params?: any) =>
    api.get('/categories', { params }),
  
  getTree: (params?: any) =>
    api.get('/categories/tree', { params }),
  
  getById: (categoryId: string) =>
    api.get(`/categories/id/${categoryId}`),
  
  getBySlug: (slug: string) =>
    api.get(`/categories/${slug}`),
};

// Lead API
export const leadAPI = {
  getAll: (params?: any) =>
    api.get('/leads/vendor', { params }),
  
  getById: (leadId: string) =>
    api.get(`/leads/${leadId}`),
  
  updateStatus: (leadId: string, data: any) =>
    api.patch(`/leads/${leadId}/status`, data),
};

// Upload API
export const uploadAPI = {
  uploadImage: (formData: FormData) =>
    api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  uploadDocument: (formData: FormData) =>
    api.post('/upload/document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  uploadStoreImages: (storeId: string, formData: FormData) =>
    api.post(`/stores/${storeId}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  uploadListingImages: (listingId: string, formData: FormData) =>
    api.post(`/listings/${listingId}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
};

export default api;